import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { Sucursal } from '../../utils/sucursalContext';

type CameraRole = 'entrada' | 'salida';
type ConnectionState = 'sin_configurar' | 'en_linea' | 'desconectada' | 'error';

type AccessCamera = {
  id: string;
  gimnasio_id: string;
  sucursal_id: string;
  nombre: string;
  numero_serie: string;
  ip_local: string;
  puerto: number;
  tipo: CameraRole;
  modelo: string | null;
  usuario_admin: string;
  activa: boolean;
  validar_membresia: boolean;
  permitir_salida_sin_validacion: boolean;
  timeout_segundos: number;
  conector_id: string | null;
  estado_conexion: ConnectionState;
  ultimo_contacto_at: string | null;
  ultimo_error: string | null;
};

type FormState = {
  nombre: string;
  numero_serie: string;
  sucursal_id: string;
  ip_local: string;
  puerto: string;
  tipo: CameraRole;
  modelo: string;
  usuario_admin: string;
  conector_id: string;
  activa: boolean;
  validar_membresia: boolean;
  permitir_salida_sin_validacion: boolean;
  timeout_segundos: string;
};

const emptyForm = (sucursalId = ''): FormState => ({
  nombre: '', numero_serie: '', sucursal_id: sucursalId, ip_local: '', puerto: '80',
  tipo: 'entrada', modelo: 'AiFace', usuario_admin: 'admin', activa: true,
  conector_id: 'sede-principal',
  validar_membresia: true, permitir_salida_sin_validacion: true, timeout_segundos: '5',
});

const stateLabels: Record<ConnectionState, { label: string; cls: string }> = {
  sin_configurar: { label: 'Sin conector', cls: 'bg-gray-100 text-gray-600' },
  en_linea: { label: 'En línea', cls: 'bg-emerald-100 text-emerald-700' },
  desconectada: { label: 'Desconectada', cls: 'bg-amber-100 text-amber-700' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-700' },
};

export default function AccessControlSettings({ gimnasioId, sucursales }: { gimnasioId: string | null; sucursales: Sucursal[] }) {
  const [cameras, setCameras] = useState<AccessCamera[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(() => emptyForm(sucursales[0]?.id));

  const branchById = useMemo(() => new Map(sucursales.map((branch) => [branch.id, branch.nombre])), [sucursales]);

  const loadCameras = useCallback(async (silent = false) => {
    if (!gimnasioId) { setError('No se pudo determinar el gimnasio activo.'); setLoading(false); return; }
    if (!silent) { setLoading(true); setError(null); }
    const { data, error: queryError } = await supabase
      .from('control_acceso_camaras')
      .select('*')
      .eq('gimnasio_id', gimnasioId)
      .order('tipo')
      .order('nombre');
    if (queryError && !silent) {
      setError(queryError.message.includes('control_acceso_camaras')
        ? 'Falta aplicar la migración de Control de Acceso en Supabase.'
        : queryError.message);
      setCameras([]);
    } else if (!queryError) setCameras((data || []) as AccessCamera[]);
    if (!silent) setLoading(false);
  }, [gimnasioId]);

  useEffect(() => {
    void loadCameras();
    const refreshTimer = window.setInterval(() => { void loadCameras(true); }, 10_000);
    return () => window.clearInterval(refreshTimer);
  }, [loadCameras]);

  const startCreate = () => {
    setEditingId(null); setForm(emptyForm(sucursales[0]?.id || '')); setShowForm(true); setError(null);
  };

  const startEdit = (camera: AccessCamera) => {
    setEditingId(camera.id);
    setForm({
      nombre: camera.nombre, numero_serie: camera.numero_serie, sucursal_id: camera.sucursal_id,
      ip_local: camera.ip_local, puerto: String(camera.puerto), tipo: camera.tipo,
      modelo: camera.modelo || '', usuario_admin: camera.usuario_admin, activa: camera.activa,
      conector_id: camera.conector_id || 'sede-principal',
      validar_membresia: camera.validar_membresia,
      permitir_salida_sin_validacion: camera.permitir_salida_sin_validacion,
      timeout_segundos: String(camera.timeout_segundos),
    });
    setShowForm(true); setError(null);
  };

  const saveCamera = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!gimnasioId) return;
    if (!form.sucursal_id) return setError('Selecciona una sucursal.');
    setSaving(true); setError(null);
    const payload = {
      gimnasio_id: gimnasioId, sucursal_id: form.sucursal_id, nombre: form.nombre.trim(),
      numero_serie: form.numero_serie.trim(), ip_local: form.ip_local.trim(), puerto: Number(form.puerto),
      tipo: form.tipo, modelo: form.modelo.trim() || null, usuario_admin: form.usuario_admin.trim() || 'admin',
      conector_id: form.conector_id.trim() || null,
      activa: form.activa, validar_membresia: form.validar_membresia,
      permitir_salida_sin_validacion: form.permitir_salida_sin_validacion,
      timeout_segundos: Number(form.timeout_segundos),
    };
    const operation = editingId
      ? supabase.from('control_acceso_camaras').update(payload).eq('id', editingId).eq('gimnasio_id', gimnasioId)
      : supabase.from('control_acceso_camaras').insert(payload);
    const { error: saveError } = await operation;
    if (saveError) setError(saveError.code === '23505' ? 'La serie o la IP ya están registradas.' : saveError.message);
    else { setShowForm(false); setEditingId(null); await loadCameras(); }
    setSaving(false);
  };

  const removeCamera = async (camera: AccessCamera) => {
    if (!window.confirm(`¿Eliminar la cámara “${camera.nombre}”?`)) return;
    const { error: deleteError } = await supabase.from('control_acceso_camaras').delete().eq('id', camera.id);
    if (deleteError) setError(deleteError.message); else await loadCameras();
  };

  return (
    <div className="text-left">
      <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
        <div><h3 className="text-lg font-semibold text-gray-900">Control de Acceso</h3><p className="text-sm text-gray-500 mt-1">Cámaras faciales, entradas, salidas y conectores locales</p></div>
        <button onClick={startCreate} className="px-3 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg"><i className="ri-add-line mr-1"/>Agregar cámara</button>
      </div>
      <div className="px-6 py-5 max-h-[68vh] overflow-y-auto space-y-4">
        <div className="p-4 rounded-xl border border-blue-200 bg-blue-50 flex gap-3">
          <i className="ri-router-line text-blue-600 text-xl"/>
          <div><p className="text-sm font-semibold text-blue-900">Arquitectura híbrida</p><p className="text-xs text-blue-800 mt-1">Supabase guarda esta configuración. Un conector instalado en la red del gimnasio comunica las IP privadas con la nube y mantiene sincronizadas las biometrías.</p></div>
        </div>
        {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
        {showForm && (
          <form onSubmit={saveCamera} className="p-4 rounded-xl border border-teal-200 bg-teal-50/40 space-y-4">
            <div className="flex justify-between"><div><p className="font-semibold text-gray-900">{editingId ? 'Editar cámara' : 'Nueva cámara'}</p><p className="text-xs text-gray-500">La serie identifica los eventos; la IP se usa para sincronizar usuarios.</p></div><button type="button" onClick={() => setShowForm(false)} className="text-gray-500"><i className="ri-close-line text-xl"/></button></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Nombre"><input required maxLength={80} value={form.nombre} onChange={(e)=>setForm({...form,nombre:e.target.value})} placeholder="Entrada principal 1" className="input"/></Field>
              <Field label="Número de serie (SN)"><input required maxLength={80} value={form.numero_serie} onChange={(e)=>setForm({...form,numero_serie:e.target.value})} placeholder="AYUC04018195" className="input uppercase"/></Field>
              <Field label="Sucursal"><select required value={form.sucursal_id} onChange={(e)=>setForm({...form,sucursal_id:e.target.value})} className="input"><option value="">Seleccionar</option>{sucursales.map((s)=><option key={s.id} value={s.id}>{s.nombre}</option>)}</select></Field>
              <Field label="Función"><select value={form.tipo} onChange={(e)=>setForm({...form,tipo:e.target.value as CameraRole})} className="input"><option value="entrada">Entrada</option><option value="salida">Salida</option></select></Field>
              <Field label="IP local"><input required inputMode="decimal" value={form.ip_local} onChange={(e)=>setForm({...form,ip_local:e.target.value})} placeholder="192.168.1.20" className="input"/></Field>
              <Field label="Puerto de administración"><input required type="number" min="1" max="65535" value={form.puerto} onChange={(e)=>setForm({...form,puerto:e.target.value})} className="input"/></Field>
              <Field label="Modelo"><input value={form.modelo} onChange={(e)=>setForm({...form,modelo:e.target.value})} placeholder="AiFace" className="input"/></Field>
              <Field label="Usuario administrativo"><input value={form.usuario_admin} onChange={(e)=>setForm({...form,usuario_admin:e.target.value})} placeholder="admin" className="input"/></Field>
              <Field label="ID del conector local"><input required value={form.conector_id} onChange={(e)=>setForm({...form,conector_id:e.target.value})} placeholder="sede-principal" className="input"/></Field>
              <Field label="Timeout de validación"><div className="flex items-center gap-2"><input type="number" min="1" max="30" value={form.timeout_segundos} onChange={(e)=>setForm({...form,timeout_segundos:e.target.value})} className="input"/><span className="text-sm text-gray-500">seg.</span></div></Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Check label="Cámara activa" checked={form.activa} onChange={(v)=>setForm({...form,activa:v})}/>
              <Check label="Validar membresía" checked={form.validar_membresia} onChange={(v)=>setForm({...form,validar_membresia:v})}/>
              <Check label="Salida libre" checked={form.permitir_salida_sin_validacion} onChange={(v)=>setForm({...form,permitir_salida_sin_validacion:v})}/>
            </div>
            <div className="flex justify-end gap-2"><button type="button" onClick={()=>setShowForm(false)} className="px-4 py-2 border border-gray-300 bg-white rounded-lg text-sm">Cancelar</button><button disabled={saving} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm disabled:opacity-60">{saving ? 'Guardando…' : 'Guardar cámara'}</button></div>
          </form>
        )}
        {loading ? <div className="py-12 text-center text-gray-500"><i className="ri-loader-4-line animate-spin text-2xl"/><p className="mt-2 text-sm">Cargando cámaras…</p></div>
        : cameras.length === 0 ? <div className="py-12 text-center border border-dashed border-gray-300 rounded-xl"><i className="ri-camera-off-line text-3xl text-gray-400"/><p className="mt-2 font-medium text-gray-700">No hay cámaras configuradas</p><p className="text-sm text-gray-500">Agrega las cámaras de entrada y salida de esta sede.</p></div>
        : <div className="space-y-3">{cameras.map((camera) => { const status=stateLabels[camera.estado_conexion]; return <div key={camera.id} className="p-4 border border-gray-200 rounded-xl flex flex-col md:flex-row md:items-center gap-4"><div className={`w-11 h-11 rounded-xl grid place-items-center ${camera.tipo==='entrada'?'bg-emerald-100 text-emerald-700':'bg-blue-100 text-blue-700'}`}><i className={camera.tipo==='entrada'?'ri-login-box-line text-xl':'ri-logout-box-line text-xl'}/></div><div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-2"><p className="font-semibold text-gray-900">{camera.nombre}</p><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${status.cls}`}>{status.label}</span>{!camera.activa&&<span className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-600">Inactiva</span>}</div><p className="text-xs text-gray-500 mt-1">{branchById.get(camera.sucursal_id) || 'Sucursal'} · {camera.tipo.toUpperCase()} · {camera.ip_local}:{camera.puerto}</p><p className="text-xs text-gray-400 mt-0.5">SN {camera.numero_serie}{camera.conector_id ? ` · Conector ${camera.conector_id}` : ''}</p></div><div className="flex gap-1"><button onClick={()=>startEdit(camera)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600" title="Editar"><i className="ri-pencil-line"/></button><button onClick={()=>removeCamera(camera)} className="p-2 hover:bg-red-50 rounded-lg text-red-600" title="Eliminar"><i className="ri-delete-bin-line"/></button></div></div>})}</div>}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) { return <label className="block"><span className="block text-xs font-medium text-gray-600 mb-1.5">{label}</span>{children}</label>; }
function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value:boolean)=>void }) { return <label className="flex items-center gap-2 p-3 rounded-lg bg-white border border-gray-200 text-sm text-gray-700"><input type="checkbox" checked={checked} onChange={(e)=>onChange(e.target.checked)} className="rounded text-teal-600"/>{label}</label>; }
